import React, { useMemo, useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { fadeUp, viewportOnce, usePrefersReducedMotion } from "@/lib/motion";

export const Container = ({ className, children, ...rest }) => (
  <div className={cn("container-x", className)} {...rest}>
    {children}
  </div>
);

// Eyebrow labels and chapter numbers were removed by request; both are kept as no-ops so call sites stay stable.
export const Eyebrow = () => null;
export const ChapterMark = () => null;

/**
 * Scroll-linked fly-in. The element travels in from the edge of the screen (default: left)
 * and settles into place as it scrolls up through the lower half of the viewport, so the
 * motion is driven by — and reversible with — the user's scroll rather than a one-shot trigger.
 * Parent should clip horizontal overflow (e.g. `overflow-hidden` on the section).
 */
export const FlyIn = ({ children, className, from = "left", distance = "70vw", settleAt = 0.5, ...rest }) => {
  const reduced = usePrefersReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", `start ${settleAt}`] });
  const progress = useSpring(scrollYProgress, { stiffness: 110, damping: 26, mass: 0.5, restDelta: 0.001 });
  const sign = from === "left" ? "-" : "";
  const x = useTransform(progress, [0, 1], [`${sign}${distance}`, "0vw"]);
  const opacity = useTransform(progress, [0, 0.55, 1], [0, 0.9, 1]);
  const rotate = useTransform(progress, [0, 1], [from === "left" ? -1.2 : 1.2, 0]);

  if (reduced) {
    return (
      <div ref={ref} className={className} {...rest}>
        {children}
      </div>
    );
  }
  return (
    <motion.div ref={ref} className={cn("will-change-transform", className)} style={{ x, opacity, rotate }} {...rest}>
      {children}
    </motion.div>
  );
};

export const Reveal = ({ children, className, delay = 0, as = "div", once = true, ...rest }) => {
  const reduced = usePrefersReducedMotion();
  const viewport = useMemo(() => ({ ...viewportOnce, once }), [once]);
  const Comp = motion[as] || motion.div;
  if (reduced) {
    const Plain = as;
    return (
      <Plain className={className} {...rest}>
        {children}
      </Plain>
    );
  }
  return (
    <Comp
      className={className}
      variants={fadeUp}
      custom={delay}
      initial="hidden"
      whileInView="show"
      viewport={viewport}
      {...rest}
    >
      {children}
    </Comp>
  );
};

/**
 * Editorial chapter header: eyebrow + chapter mark on the left, thesis on the right.
 */
export const ChapterHeader = ({
  index,
  total = 9,
  eyebrow,
  title,
  lead,
  light = false,
  className,
  titleClassName,
  stacked = false,
  testId,
}) => (
  <div
    data-testid={testId}
    className={cn(
      "mb-8 sm:mb-10 flex flex-col gap-5",
      !stacked && "lg:flex-row lg:items-end lg:justify-between lg:gap-16",
      className
    )}
  >
    <Reveal className={cn("flex flex-col", !stacked && "lg:max-w-[52%]")}>
      <h2
        className={cn(
          "font-display text-balance text-[1.9rem] leading-[1.06] tracking-[-0.02em] sm:text-[2.3rem] lg:text-[2.7rem]",
          light ? "text-white" : "text-[color:var(--ink)]",
          titleClassName
        )}
      >
        {title}
      </h2>
    </Reveal>
    {lead ? (
      <Reveal delay={1} className={cn("text-pretty", !stacked && "lg:max-w-[42%] lg:pb-1")}>
        <p className={cn("text-[1rem] leading-[1.6] sm:text-[1.05rem]", light ? "text-[#D9DEE5]" : "text-[color:var(--muted-ink)]")}>
          {lead}
        </p>
      </Reveal>
    ) : null}
  </div>
);

const base =
  "btn-arrow group inline-flex items-center justify-center gap-2 text-center whitespace-normal sm:whitespace-nowrap rounded-xl font-medium tracking-[-0.005em] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--blue)] focus-visible:ring-offset-2 disabled:opacity-60 disabled:pointer-events-none";

const variants = {
  primary: "bg-[color:var(--navy)] text-white hover:bg-[color:var(--navy-2)] shadow-[0_8px_24px_rgba(44,62,80,0.18)]",
  gold: "bg-[color:var(--gold)] text-[color:var(--navy-3)] hover:bg-[#E9AE00] shadow-[0_8px_24px_rgba(245,184,0,0.25)]",
  secondary: "border border-[color:var(--navy)] text-[color:var(--navy)] bg-white hover:bg-[color:var(--navy)] hover:text-white",
  secondaryLight: "border border-white/70 text-white bg-[color:var(--navy-2)] hover:bg-white hover:text-[color:var(--navy)]",
  ghost: "text-[color:var(--navy)] hover:text-[color:var(--blue)] px-0",
  ghostLight: "text-white hover:text-[color:var(--gold)] px-0",
};

const sizes = {
  md: "min-h-[2.75rem] px-5 py-2 text-[0.95rem]",
  lg: "min-h-[3rem] px-6 py-2.5 text-base",
  xl: "min-h-[3.5rem] px-6 py-3 text-base sm:px-7 sm:text-[1.05rem]",
};

export const CTAButton = React.forwardRef(
  ({ variant = "primary", size = "lg", className, children, icon = "up", href, as, testId, ...rest }, ref) => {
    const Icon = icon === "right" ? ArrowRight : ArrowUpRight;
    const cls = cn(base, variants[variant], variant.startsWith("ghost") ? "h-auto" : sizes[size], className);
    if (href) {
      return (
        <a ref={ref} href={href} className={cls} data-testid={testId} {...rest}>
          <span>{children}</span>
          {icon ? <Icon className="h-4 w-4" aria-hidden="true" /> : null}
        </a>
      );
    }
    return (
      <button ref={ref} type="button" className={cls} data-testid={testId} {...rest}>
        <span>{children}</span>
        {icon ? <Icon className="h-4 w-4" aria-hidden="true" /> : null}
      </button>
    );
  }
);
CTAButton.displayName = "CTAButton";

export const Section = ({ id, navId, className, children, ...rest }) => (
  <section
    id={id}
    data-section-id={navId}
    className={cn("relative", className)}
    {...rest}
  >
    {children}
  </section>
);

/** Small chip with a coloured dot, used for segment axis levels. */
export const LevelChip = ({ label, value, color, className, testId }) => (
  <span
    data-testid={testId}
    className={cn(
      "inline-flex items-center gap-2 rounded-full border border-[color:var(--hairline)] bg-white px-3 py-1.5 text-[0.8rem] text-[color:var(--muted-ink)]",
      className
    )}
  >
    <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ background: color }} />
    <span className="font-mono text-[10px] uppercase tracking-[0.14em]">{label}</span>
    <span className="font-medium text-[color:var(--ink)]">{value}</span>
  </span>
);
